import type { ReactElement, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import PrimaryLink from 'components/atoms/primary-link/PrimaryLink';
import styles from './SiteFooter.module.scss';
import { setupFooterListTransition } from './SiteFooter.transitions';
import type { SiteFooterProps } from './SiteFooter.data';
import Icon from '../../atoms/icon/Icon';
import { isGeofencedAhrefLink } from '../../../util/countries.utils';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import { useGlobalStore } from '../../../stores/global';

export default function SiteFooter({ ref }: SiteFooterProps): ReactElement {
  const store = useGlobalStore();
  const { globalSettings, countryCode } = store;

  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const socialElementRef = useRef<HTMLUListElement>(null);
  const legalElementRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLElement>(null);

  const socialListItemsRef = useRef<Array<HTMLElement>>([]);
  const legalListItemsRef = useRef<Array<HTMLElement>>([]);

  const primaryStaggerRefItems = useRef<Array<HTMLElement>>([]);
  const secondaryStaggerRefItems = useRef<Array<HTMLElement>>([]);

  useEffect(() => {
    primaryStaggerRefItems.current = [logoRef.current as HTMLElement];
    secondaryStaggerRefItems.current = [...socialListItemsRef.current];
  }, []);

  useScrollTrigger({
    trigger: elementRef,
    vars: { once: true },
    animation: useRef(
      useSetupTimeline(setupFooterListTransition, {
        listItemsRef: primaryStaggerRefItems,
      }),
    ).current,
  });

  useScrollTrigger({
    trigger: socialElementRef,
    vars: { once: true },
    animation: useRef(
      useSetupTimeline(setupFooterListTransition, { listItemsRef: secondaryStaggerRefItems }),
    ).current,
  });

  useScrollTrigger({
    trigger: legalElementRef,
    vars: { once: true },
    animation: useRef(
      useSetupTimeline(setupFooterListTransition, { listItemsRef: legalListItemsRef }),
    ).current,
  });

  // todo refactor to split in child-components for better overview.
  return (
    <footer ref={elementRef} className={styles.siteFooter}>
      <div className={classNames(styles.contentWrapper)}>
        <div className={styles.dcLogo}>
          <Icon ref={logoRef} name="logo" className={classNames(styles.logo)} />
        </div>

        <div className={styles.footerLinks}>
          <ul ref={socialElementRef} className={classNames(styles.socialList)}>
            {globalSettings?.social_links?.map((aHrefLink, index) => (
              <li
                ref={(ref) => {
                  socialListItemsRef.current[index] = ref as HTMLElement;
                }}
                key={`social_${index}`}
                className={classNames(styles.socialListItem)}
              >
                <PrimaryLink
                  textVariant="button-label-small"
                  className={classNames(styles.socialAnchor)}
                  link={{ href: aHrefLink.link.cached_url, uuid: aHrefLink.link.id }}
                  label={aHrefLink.title}
                  target={aHrefLink.link.target}
                />
              </li>
            ))}
            <li
              className={classNames(styles.socialListItem)}
              ref={(ref) => {
                socialListItemsRef.current[globalSettings?.social_links?.length || 0] =
                  ref as HTMLElement;
              }}
            >
              {globalSettings?.dentsu?.[0].icon?.filename && (
                <div
                  ref={(ref) => {
                    legalListItemsRef.current.push(ref as HTMLElement);
                  }}
                  className={classNames(styles.legalListItem, styles.dentsuLogo)}
                >
                  <a
                    href={globalSettings.dentsu[0].link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src={globalSettings.dentsu[0].icon.filename}
                      alt="a dentsu company"
                      fill
                    />
                  </a>
                </div>
              )}
            </li>
          </ul>

          <div ref={legalElementRef} className={classNames(styles.legalWrapper)}>
            <ul className={classNames(styles.legalList)}>
              {globalSettings?.legal_links
                ?.filter((link) => {
                  let visible = true;
                  if (isGeofencedAhrefLink(link)) {
                    if (link.countries?.length > 0) {
                      if (link.exclude_countries) {
                        visible = !link.countries?.includes(countryCode);
                      } else {
                        visible = link.countries?.includes(countryCode);
                      }
                    }
                  }

                  return visible;
                })
                .map((aHrefLink, index) => (
                  <li
                    ref={(ref) => {
                      legalListItemsRef.current[index] = ref as HTMLElement;
                    }}
                    key={`legal_${index}`}
                    className={classNames(styles.legalListItem)}
                  >
                    <PrimaryLink
                      textVariant="foot-note"
                      className={classNames(styles.legalAnchor)}
                      link={{ href: aHrefLink.link.cached_url, uuid: aHrefLink.link.id }}
                      label={aHrefLink.title}
                      target={aHrefLink.link.target}
                    />
                  </li>
                ))}
              {globalSettings?.copyright && (
                <li
                  ref={(ref) => {
                    legalListItemsRef.current.push(ref as HTMLElement);
                  }}
                  className={classNames(styles.legalListItem)}
                >
                  {globalSettings.copyright.replace(/\b20\d{2}\b/, `${new Date().getFullYear()}`)}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
